import {
  ConstrainMode,
  DetailsRow,
  IColumn,
  IDetailsListProps,
  IObjectWithKey,
  IPlainCardProps,
} from '@fluentui/react';
import AppContext from 'App/AppContext';
import { IPFontIcon, IPScrollablePane, IPShimmeredDetailsList, IPText, IPTooltipHost } from 'baseComponents';
import { IPDetailsListLayoutMode } from 'baseComponents/DetailsList/IPDetailsList';
import { IPDirectionalHint } from 'baseComponents/Dialog/IPDialog';
import { IPHoverCard, IPHoverCardType } from 'baseComponents/HoverCard/IPHoverCard';
import { IPPersona, IPPersonaSize } from 'baseComponents/Persona/IPPersona';
import { IPScrollbarVisibility } from 'baseComponents/ScrollablePane/IPScrollablePane';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { IPSelection, IPSelectionMode } from 'baseComponents/Utils/Selection';
import CheckList from 'components/Checklist/Checklist';
import ChecklistProgress from 'components/Checklist/ChecklistProgress';
import SelectColumnsDialog from 'components/Dialogs/SelectColumnsDialog';
import HrefLink from 'components/Links/HrefLink';
import { KeyValueTags } from 'components/Tags/KeyValueTag';
import { ColumnCellItems } from 'components/Utils/ColumnCellItems';
import { onRenderDetailsHeaderGlobal } from 'globalFunctions';
import {
  globalStackItemStylesPaddingSceneScroll,
  globalStackTokensGapExtraSmall,
  globalStackTokensGapSmall,
  globalTextStylesError,
} from 'globalStyles';
import Entity from 'models/entity';
import { isAuditCriteriaPreviewEnabled } from 'models/copilot/copilotPreviewFeatureToggles';
import Tag from 'models/tag';
import Task, { TaskTypes } from 'models/tasks/task';
import { TaskKinds } from 'models/tasks/taskType';
import { TaskRelation, TaskRelationship } from 'models/tasks/taskRelation';
import User from 'models/user';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { FeatureTypes, hasUserFeature } from 'services/Auth/featurePermissions';
import { IPSharedColors } from 'themes/runtime/fluentColors';
import { filterColumnsFromLocalStorage } from 'utils/columns';
import { toLocaleDateShort } from 'utils/datetime';
import { getHtmlJSX } from 'utils/html';
import { getLocalStorageData, LocalStorageKeys, setLocalStorageData } from 'utils/localstorage';
import { sortOnDate, sortOnNumber, sortOnString, sortOnTaskContext, sortOnTaskTags } from 'utils/sorting';
import { overflow } from 'utils/string';
import { FilterTaskGroupKeys } from '../Filter/FilterTaskGroupKeys';
import { onRenderTaskContext } from './TaskRenderHelpers';

interface ITasksListProps {
  tasks: Task[];
  filter: string[];
  isLoading: boolean;
  hideColumns?: string[];
  onTaskClick: (task: Task) => void;
  updateFilter?: (filter: string[]) => void;
  cacheKeySort: LocalStorageKeys;
  selectionMode?: IPSelectionMode;
  onChangeSelection?: (tasks: Task[]) => void;
  showColumnSelector?: boolean;
  hideColumnSelector?: () => void;
  selectionResetKey?: string;
}

const TaskList = (props: ITasksListProps) => {
  //load translation files for context items
  const { t } = useTranslation([
    'translation',
    'task',
    'tasks',
    'asset',
    'risk',
    'control',
    'theme',
    'objective',
    'process',
    'norms',
    'adminAuth',
  ]);
  const appContext = useContext(AppContext);
  const history = useHistory();
  const [tasksDisplay, setTasksDisplay] = useState<Task[]>([]);
  const [sortLoaded, setSortLoaded] = useState<boolean>(false);
  const [selectionResetKey, setSelectionResetKey] = useState<string>('set');
  // The columns live in state and are built once at mount, so their render closures keep the props
  // of that first render. Handlers inside a column read the latest props through this ref, otherwise
  // a tag click toggles against the filter of the view that was active when the grid first mounted.
  const propsRef = useRef(props);
  propsRef.current = props;

  const selection = new IPSelection({
    getKey: (item: Task) => {
      return item.taskId;
    },
    onSelectionChanged: () => {
      if (props.onChangeSelection) {
        const items = selection.getSelection() as Task[];
        props.onChangeSelection(items.filter((t) => t !== undefined));
      }
    },
  });

  //
  // Effects
  //
  useEffect(() => {
    if (props.tasks && props.tasks.length > 0 && !sortLoaded) {
      //apply 1 time sort on changed tasks
      setTasksDisplay(loadSortFromLocalStorage(props.tasks));
    } else {
      setTasksDisplay(applyCurrentSort(props.tasks));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.tasks]);

  useEffect(() => {
    setTasksDisplay(applyCurrentSort(props.tasks));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.filter]);

  useEffect(() => {
    if (props.selectionResetKey) setSelectionResetKey(props.selectionResetKey);
  }, [props.selectionResetKey]);

  //
  // Helpers
  //
  const setTagFilter = (tag: Tag) => {
    const { filter, updateFilter } = propsRef.current;
    let newFilter = [...filter];
    const filterId = FilterTaskGroupKeys.tag + tag.tagId;
    if (newFilter.indexOf(filterId) >= 0) {
      newFilter = newFilter.filter((f) => f !== filterId);
    } else {
      newFilter.push(filterId);
    }

    if (updateFilter) {
      updateFilter(newFilter);
    }
  };

  //
  // Columns
  //
  const getColumns = (applyFilter: boolean): IColumn[] => {
    let allColumns: IColumn[] = [
      {
        key: 'name',
        name: t('tasks:TabAllTasks.Fields.Name'),
        minWidth: 200,
        maxWidth: 400,
        isMultiline: true,
        isResizable: true,
        isSorted: false,
        onRender: (item?: Task, index?: number, column?: IColumn) => {
          if (!item) return;

          return (
            <IPTooltipHost
              content={getHtmlJSX(item.description, true, true) || undefined}
              calloutProps={{ directionalHint: IPDirectionalHint.rightBottomEdge }}
            >
              <IPText>
                <HrefLink
                  underline
                  href={`${item.taskId}`}
                  onClick={(e) => {
                    props.onTaskClick(item);
                  }}
                >
                  {overflow(item?.name, 100)}
                </HrefLink>
              </IPText>
            </IPTooltipHost>
          );
        },
      },
      {
        key: 'taskId',
        name: t('tasks:TabAllTasks.Fields.Id'),
        minWidth: 40,
        maxWidth: 70,
        isResizable: true,
        isSorted: false,
        data: { defaultHidden: true },
        onRender: (item?: Task) => {
          if (!item) return null;

          return <IPText variant="small">{item.taskId}</IPText>;
        },
      },
      {
        key: 'assignee',
        name: t('tasks:TabAllTasks.Fields.Assignee'),
        minWidth: 30,
        maxWidth: 150,
        isResizable: true,
        isSorted: false,
        onRender: (item?: Task, index?: number, column?: IColumn) => {
          if (!item) return null;
          if (item.taskType === TaskTypes.Event && item.isSeries()) return null; //event series cannot be assigned

          let user: User | undefined = undefined;
          if (item.userId) {
            user = appContext.globalDataCache.users.get(item?.userId);
          }

          return (
            <IPTooltipHost content={user?.name}>
              <IPPersona
                imageUrl={user?.getPhotoURLObj()}
                size={IPPersonaSize.size24}
                text={user?.name || ''}
                hidePersonaDetails={(column?.currentWidth ?? 0) < 100}
              ></IPPersona>
            </IPTooltipHost>
          );
        },
      },
      {
        key: 'status',
        name: t('tasks:TabAllTasks.Fields.Status'),
        minWidth: 80,
        maxWidth: 120,
        isResizable: true,
        isSorted: false,
        onRender: (item?: Task, index?: number, column?: IColumn) => {
          if (!item) return null;

          const status = appContext.globalDataCache.taskStates.get(item.taskStateId);

          return <IPText>{status ? status?.state : ''}</IPText>;
        },
      },
      {
        key: 'taskType',
        name: t('tasks:TabAllTasks.Fields.Type'),
        minWidth: 80,
        maxWidth: 160,
        isResizable: true,
        isSorted: false,
        onRender: (item?: Task) => {
          if (!item) return null;

          // an assessment task names its kind beside its type (PLAN-075): a single task or a recurring one alike
          if (item.taskKind === TaskKinds.Assessment && isAuditCriteriaPreviewEnabled(appContext)) {
            return (
              <IPStack horizontal verticalAlign="center" tokens={globalStackTokensGapExtraSmall}>
                <IPFontIcon iconName="ComplianceAudit" />
                <IPText>
                  {t('tasks:Kinds.Assessment', { type: Task.getWorkableTypeText(item.getWorkableTaskType(), t) })}
                </IPText>
              </IPStack>
            );
          }

          return <IPText>{Task.getWorkableTypeText(item.getWorkableTaskType(), t)}</IPText>;
        },
      },
      {
        key: 'checklist',
        name: t('task:Pivot.Checklist'),
        minWidth: 80,
        maxWidth: 200,
        isResizable: true,
        isSorted: false,
        onRender: (item?: Task, index?: number, column?: IColumn) => {
          if (!item || item.taskTypeId || item.checkList.items.length === 0) return null;

          const plainCardProps: IPlainCardProps = {
            onRenderPlainCard: onRenderChecklistHoverCard,
            renderData: item,
          };

          return (
            <IPHoverCard type={IPHoverCardType.plain} plainCardProps={plainCardProps} instantOpenOnClick={true}>
              <ChecklistProgress
                compact
                totalChecks={item.checkList.items.length}
                successChecks={item.checkList.getSuccessfulCount()}
                failedChecks={item.checkList.getFailedCount()}
              />
            </IPHoverCard>
          );
        },
      },
      {
        key: 'tags',
        name: t('tasks:TabAllTasks.Fields.Tags'),
        minWidth: 100,
        maxWidth: 200,
        isResizable: true,
        onRender: (item?: Task, index?: number, column?: IColumn) => {
          return (
            <KeyValueTags
              tags={appContext.globalDataCache.tags.getItemsForId(item?.tagIds)}
              onClick={(tag) => setTagFilter(tag)}
            />
          );
        },
      },
      {
        key: 'start',
        name: t('tasks:TabAllTasks.Fields.Date'),
        minWidth: 80,
        maxWidth: 100,
        isResizable: true,
        isSorted: false,
        onRender: (item?: Task, index?: number, column?: IColumn) => {
          if (!item) return null;

          return (
            <IPStack horizontal tokens={globalStackTokensGapSmall}>
              <IPText variant="small">{toLocaleDateShort(item.startDateTime)}</IPText>
              {item.recurringPattern && item.recurringPattern.isActive && <IPFontIcon iconName="Sync" />}
            </IPStack>
          );
        },
      },
      {
        key: 'completed',
        name: t('tasks:TabAllTasks.Fields.Completed'),
        minWidth: 80,
        maxWidth: 100,
        isResizable: true,
        isSorted: false,
        onRender: (item?: Task, index?: number, column?: IColumn) => {
          if (!item) return null;

          return <IPText variant="small">{toLocaleDateShort(item.completed)}</IPText>;
        },
      },
      {
        key: 'deadline',
        name: t('tasks:TabAllTasks.Fields.Deadline'),
        minWidth: 80,
        maxWidth: 100,
        isResizable: true,
        isSorted: false,
        onRender: (item?: Task, index?: number, column?: IColumn) => {
          if (!item) return null;
          if (item.taskType === TaskTypes.Event && item.isSeries()) return null; //event series cannot be assigned

          const deadline = item.getDeadline(appContext.globalDataCache);
          let overDeadline = false;
          if (!item.completed) {
            overDeadline = item.isOverDeadline(deadline, new Date());
          }

          return (
            <IPText variant="small" styles={overDeadline ? globalTextStylesError : undefined}>
              {toLocaleDateShort(deadline)}
            </IPText>
          );
        },
      },
      {
        key: 'followUp',
        name: t('tasks:TabAllTasks.Fields.FollowUp'),
        minWidth: 50,
        maxWidth: 100,
        isResizable: true,
        isSorted: false,
        onRender: (item?: Task) => {
          if (!item || (!item.hasFollowUp && !item.hasUnlinkedFollowUp)) return null;

          if (item.hasUnlinkedFollowUp) {
            const unlinked: TaskRelation | undefined = item.taskRelations?.find(
              (r) => r.relationship === TaskRelationship.FollowUp && r.isUnlinked(),
            );
            const assignee: User | undefined = unlinked?.notifyUserId
              ? appContext.globalDataCache.users.get(unlinked.notifyUserId)
              : undefined;
            const tooltip = assignee?.name
              ? t('task:Context.RelationTexts.FollowUpPendingAssignedTo', { name: assignee.name })
              : t('task:Context.RelationTexts.FollowUpPending');

            return (
              <IPTooltipHost content={tooltip}>
                <HrefLink
                  href={`${item.taskId}?tab=Context`}
                  onClick={() => {
                    history.push(`/tasks/${item.taskId}?tab=Context`);
                  }}
                >
                  <IPFontIcon
                    iconName="SkypeCircleClock"
                    style={{ fontSize: 14, color: IPSharedColors.orange20, cursor: 'pointer' }}
                  />
                </HrefLink>
              </IPTooltipHost>
            );
          }

          const total = item.followUpTotal;
          const completed = item.followUpCompleted;
          const allComplete = total > 0 && completed === total;

          return (
            <IPStack horizontal verticalAlign="center" tokens={globalStackTokensGapSmall}>
              <IPFontIcon
                iconName={allComplete ? 'SkypeCircleCheck' : 'CircleHalfFull'}
                style={{ fontSize: 14, color: allComplete ? IPSharedColors.green20 : IPSharedColors.cyanBlue10 }}
              />
              <IPText variant="small">{`${completed}/${total}`}</IPText>
            </IPStack>
          );
        },
      },
      {
        key: 'context',
        name: t('tasks:TaskView.Schedule.Columns.Context'),
        minWidth: 100,
        maxWidth: 300,
        isMultiline: true,
        isResizable: true,
        onRender: (item?: Task, index?: number, column?: IColumn) => {
          if (!item) return;
          let entityCount: number;
          const entity: Entity | undefined = undefined;

          entityCount = item.riskIds.length;
          entityCount += item.controlIds.length;
          entityCount += item.processIds.length;
          entityCount += item.objectiveIds.length;
          entityCount += item.assetIds.length;
          entityCount += item.processingActivityIds.length;
          entityCount += item.taskRelations?.length ?? 0;

          if (entityCount > 0) {
            const plainCardProps: IPlainCardProps = {
              onRenderPlainCard: onRenderTaskContext,
              renderData: item,
            };

            return (
              <IPHoverCard type={IPHoverCardType.plain} plainCardProps={plainCardProps} instantOpenOnClick={true}>
                <IPText variant="small">
                  <HrefLink entity={entity} underline>
                    {entityCount.toString() + ' items'}
                  </HrefLink>
                </IPText>
              </IPHoverCard>
            );
          } else {
            return null;
          }
        },
      },
      {
        key: 'standards',
        name: t('norms:List.ColumnHeader'),
        minWidth: 150,
        maxWidth: 250,
        isResizable: true,
        onRender: (item?: Task, index?: number, column?: IColumn) => {
          if (!item || !item.normIds || item.normIds.length === 0) return;
          const norms = appContext.globalDataCache.norms.getItemsForId(item.normIds);

          return (
            <ColumnCellItems
              items={norms.map((n) => {
                return { id: n.normId, text: n.name };
              })}
              maxItems={3}
              showAll={undefined}
            />
          );
        },
      },
    ];

    if (hasUserFeature(appContext, FeatureTypes.RBAC)) {
      allColumns.push({
        key: 'permissions',
        name: t('adminAuth:AuthSchemaPicker.Label'),
        minWidth: 100,
        maxWidth: 350,
        isResizable: true,
        onRender: (item?: Task, index?: number, column?: IColumn) => {
          if (!item) return null;
          if (!item.authSchemaId) {
            return <IPText>{t('adminAuth:AuthSchemaPicker.Placeholder')}</IPText>;
          } else {
            const schema = appContext.globalDataCache.authSchemas.get(item.authSchemaId);

            return <IPText>{schema.name ?? t('adminAuth:AuthSchemaPicker.Placeholder')}</IPText>;
          }
        },
      });
    }

    if (props.hideColumns && props.hideColumns.length > 0) {
      allColumns = allColumns.filter((c) => props.hideColumns!.indexOf(c.key) === -1);
    }

    if (applyFilter) {
      return filterColumnsFromLocalStorage(appContext, LocalStorageKeys.TaskColumns, allColumns);
    } else {
      return allColumns;
    }
  };

  const [columns, setColumns] = useState<IColumn[]>(getColumns(true));

  const onRenderChecklistHoverCard = (item: Task): JSX.Element => {
    return (
      <IPStack styles={{ root: { width: '30vw', maxWidth: '95vw', height: 400, padding: 20 } }}>
        <CheckList
          allowEdit={false}
          allowFill={false}
          allowComment={false}
          checkList={item.checkList}
          updateCheckList={() => {}}
        />
      </IPStack>
    );
  };

  //
  // Sorting
  //
  const applyCurrentSort = (tasks: Task[]): Task[] => {
    const col = getSortedColumn(columns);
    if (col) {
      tasks = copyAndSort(tasks, col.key, col.isSortedDescending);
    }

    return tasks;
  };

  const getSortedColumn = (columns: IColumn[]): IColumn | undefined => {
    return columns.find((col) => col.isSorted === true);
  };

  const loadSortFromLocalStorage = (tasks: Task[]): Task[] => {
    if (props.cacheKeySort) {
      setSortLoaded(true);
      const storedSort = getLocalStorageData(appContext, props.cacheKeySort);
      if (storedSort) {
        try {
          const values = storedSort.split(',');
          const colKey = values[0];
          const descending = values[1] === '1';

          const newColumns: IColumn[] = columns.slice();
          const currColumn: IColumn = newColumns.filter((currCol) => colKey === currCol.key)[0];

          for (const newCol of newColumns) {
            if (newCol === currColumn) {
              newCol.isSortedDescending = descending;
              newCol.isSorted = true;
            } else {
              newCol.isSorted = false;
              newCol.isSortedDescending = false;
            }
          }

          setColumns(newColumns);

          return copyAndSort(tasks, colKey, descending);
        } catch {
          //ignore
        }
      }
    }

    return tasks;
  };

  const saveSortToLocalStorage = (key: string, descending: boolean) => {
    if (props.cacheKeySort) {
      const values = [key, descending ? '1' : '0'];
      setLocalStorageData(appContext, props.cacheKeySort, values.join(','));
    }
  };

  const onColumnClick = (ev: React.MouseEvent<HTMLElement> | undefined, column: IColumn | undefined): void => {
    const newColumns: IColumn[] = columns.slice();
    const currColumn: IColumn = newColumns.filter((currCol) => column?.key === currCol.key)[0];

    for (const newCol of newColumns) {
      if (newCol === currColumn) {
        newCol.isSortedDescending = !currColumn.isSortedDescending;
        newCol.isSorted = true;
      } else {
        newCol.isSorted = false;
        newCol.isSortedDescending = false;
      }
    }

    const newItems = copyAndSort(props.tasks, currColumn.key, currColumn.isSortedDescending);

    setColumns(newColumns);
    setTasksDisplay(newItems);
    saveSortToLocalStorage(currColumn.key, currColumn.isSortedDescending || false);
  };

  const copyAndSort = (items: Task[], columnKey: string, isSortedDescending?: boolean): Task[] => {
    if (columnKey === 'permissions') {
      return items.slice(0).sort((a: Task, b: Task) => {
        const cA = appContext.globalDataCache.authSchemas.get(a.authSchemaId);
        const cB = appContext.globalDataCache.authSchemas.get(b.authSchemaId);

        return (isSortedDescending ? -1 : 1) * sortOnString(cA.name, cB.name);
      });
    } else if (columnKey === 'checklist') {
      return items.slice(0).sort((a: Task, b: Task) => {
        const cA = a.checkList.getCompletedCheckCount();
        const cB = b.checkList.getCompletedCheckCount();

        return (isSortedDescending ? -1 : 1) * (cA - cB);
      });
    } else if (columnKey === 'followUp') {
      // Sort follow-up column into three tiers: linked > unlinked ("do later") > none.
      // followUpTotal only counts linked follow-ups, so an unlinked follow-up has followUpTotal=0.
      // The +1 offset ensures any linked count (1->2, 3->4, etc.) always ranks above unlinked (score=1),
      // which in turn ranks above no follow-up (score=0).
      return items.slice(0).sort((a: Task, b: Task) => {
        const scoreA = a.followUpTotal > 0 ? a.followUpTotal + 1 : a.hasUnlinkedFollowUp ? 1 : 0;
        const scoreB = b.followUpTotal > 0 ? b.followUpTotal + 1 : b.hasUnlinkedFollowUp ? 1 : 0;

        return (isSortedDescending ? -1 : 1) * sortOnNumber(scoreA, scoreB);
      });
    } else if (columnKey === 'status') {
      return items.slice(0).sort((a: Task, b: Task) => {
        const cA = appContext.globalDataCache.taskStates.get(a.taskStateId).sortOrder;
        const cB = appContext.globalDataCache.taskStates.get(b.taskStateId).sortOrder;

        return (isSortedDescending ? -1 : 1) * (cA - cB);
      });
    } else if (columnKey === 'name') {
      return items.slice(0).sort((a: Task, b: Task) => {
        return (isSortedDescending ? -1 : 1) * sortOnString(a.name, b.name);
      });
    } else if (columnKey === 'assignee') {
      return items.slice(0).sort((a: Task, b: Task) => {
        return (isSortedDescending ? -1 : 1) * sortOnString(a.user?.name, b.user?.name);
      });
    } else if (columnKey === 'deadline') {
      return items.slice(0).sort((a: Task, b: Task) => {
        return (
          (isSortedDescending ? -1 : 1) *
          sortOnDate(a.getDeadline(appContext.globalDataCache), b.getDeadline(appContext.globalDataCache))
        );
      });
    } else if (columnKey === 'start') {
      return items.slice(0).sort((a: Task, b: Task) => {
        return (isSortedDescending ? -1 : 1) * sortOnDate(a.startDateTime, b.startDateTime);
      });
    } else if (columnKey === 'completed') {
      return items.slice(0).sort((a: Task, b: Task) => {
        return (isSortedDescending ? -1 : 1) * sortOnDate(a.completed, b.completed);
      });
    } else if (columnKey === 'context') {
      return items.slice(0).sort((a: Task, b: Task) => {
        return (isSortedDescending ? -1 : 1) * sortOnTaskContext(a, b);
      });
    } else if (columnKey === 'tags') {
      return items.slice(0).sort((a: Task, b: Task) => {
        return (isSortedDescending ? -1 : 1) * sortOnTaskTags(a, b);
      });
    } else if (columnKey === 'taskType') {
      return items.slice(0).sort((a: Task, b: Task) => {
        return (
          (isSortedDescending ? -1 : 1) *
          sortOnString(
            Task.getWorkableTypeText(a.getWorkableTaskType(), t),
            Task.getWorkableTypeText(b.getWorkableTaskType(), t),
          )
        );
      });
    } else if (columnKey === 'taskId') {
      return items.slice(0).sort((a: Task, b: Task) => {
        return (isSortedDescending ? -1 : 1) * sortOnNumber(a.taskId, b.taskId);
      });
    }

    return items;
  };

  //
  // Render helpers
  //
  const onRenderRow: IDetailsListProps['onRenderRow'] = (rowProps) => {
    if (rowProps) {
      return (
        <span
          onDoubleClick={() => {
            if (props.onTaskClick) props.onTaskClick(rowProps.item);
          }}
        >
          <DetailsRow {...rowProps} />
        </span>
      );
    }

    return null;
  };

  //
  //
  // Main render
  //
  return (
    <IPStack verticalFill>
      <IPStack.Item grow styles={globalStackItemStylesPaddingSceneScroll}>
        {!props.isLoading && tasksDisplay && tasksDisplay.length === 0 && (
          <IPStack verticalFill horizontalAlign="center" verticalAlign="center">
            <IPText>{t('translation:General.Notifications.NoItemsFilter')}</IPText>
          </IPStack>
        )}
        <IPScrollablePane scrollbarVisibility={IPScrollbarVisibility.auto}>
          <IPShimmeredDetailsList
            compact
            items={tasksDisplay}
            columns={columns}
            setKey={selectionResetKey}
            isHeaderVisible={true}
            enableShimmer={props.isLoading}
            selection={selection as IPSelection<IObjectWithKey>}
            selectionMode={props.selectionMode || IPSelectionMode.none}
            selectionPreservedOnEmptyClick={true}
            layoutMode={IPDetailsListLayoutMode.justified}
            constrainMode={ConstrainMode.unconstrained}
            onColumnHeaderClick={onColumnClick}
            onRenderDetailsHeader={onRenderDetailsHeaderGlobal}
            onRenderRow={onRenderRow}
          />
        </IPScrollablePane>
      </IPStack.Item>
      {props.showColumnSelector && (
        <SelectColumnsDialog
          isOpen={props.showColumnSelector}
          onClose={() => {
            if (props.hideColumnSelector) props.hideColumnSelector();
          }}
          onSave={() => {
            if (props.hideColumnSelector) props.hideColumnSelector();
            const newColumns = getColumns(true);
            setColumns(newColumns);
          }}
          columns={getColumns(false)}
          cacheKey={LocalStorageKeys.TaskColumns}
        />
      )}
    </IPStack>
  );
};

export default TaskList;
